import React, { useEffect, useState } from 'react';
import classes from './AddCustomer.module.css';
import CustomDropdown from '../../components/UI/Inputs/CustomDropdown';
import { IIdAndLabel } from '../../components/UI/Inputs/ModalDropdown';
import Spinner from '../../components/UI/Spinner/Spinner';

import { fetchGenders, Gender, selectGenders } from '../../store/slices/gender';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fetchCountries, ICountry, selectCountries } from '../../store/slices/countries';
import {
  addCustomer,
  fetchCustomers,
  resetCustomerAddingStatus,
  selectCustomerAddingStatus,
} from '../../store/slices/customers';
import { toast } from 'react-toastify';
import { E164Number, isValidNumber } from 'libphonenumber-js';
import CustomTextField from '../../components/UI/Inputs/CustomTextField';
import PrimaryButtonV2 from '../../components/UI/Buttons/PrimaryButtonV2';
import ModalDatePickerNewDesign from '../../components/UI/Inputs/ModalDatePickerNewDesign';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { getBloodTypeOptions, getRelationshipStatusOptions } from '../../utils/translations';
import { useTranslation } from '../../hooks/useTranslations';



type Props = {
  setIsAddingPerson: (val: boolean) => void;
};
interface IValidationData {
  name: string;
  value: boolean;
}
const AddCustomer: React.FC<Props> = ({ setIsAddingPerson }) => {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();

  const genders: Gender[] = useAppSelector<Gender[]>(selectGenders);
  const countries: ICountry[] = useAppSelector<ICountry[]>(selectCountries);
  const customerAddingStatus: string = useAppSelector<string>(selectCustomerAddingStatus);
  const [isCallingAddPersonApi, setIsCallingAddPersonApi] = useState<boolean>(false);
  const [personFirstname, setPersonFirstname] = useState<string>('');
  const [personLastname, setPersonLastname] = useState<string>('');
  const [personGenderId, setPersonGenderId] = useState<number>();
  const [personBirthdate, setPersonBirthdate] = useState<Date | any>();
  const [personEmail, setPersonEmail] = useState<string>('');
  const [personMobilePhone, setPersonMobilePhone] = useState<string>('');
  const [personHeight, setPersonHeight] = useState<string>('');
  const [personWeight, setPersonWeight] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [houseNumber, setHouseNumber] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [countryId, setCountryId] = useState<string>('');
  const [hasOtherInputsUpdate, setHasOtherInputsUpdate] = React.useState<boolean>(false);
  const [personIdentityNumber, setPersonIdentityNumber] = useState<string>('');
  const [language, setLanguage] = useState<string>('de');
  const [personFirstnameValidation, setPersonFirstnameValidation] = useState<boolean>(false);
  const [personLastnameValidation, setPersonLastnameValidation] = useState<boolean>(false);
  const [personGenderIdValidation, setPersonGenderIdValidation] = useState<boolean>(false);
  const [personBirthdateValidation, setPersonBirthdateValidation] = useState<boolean>(false);
  const [personEmailValidation, setPersonEmailValidation] = useState<boolean>(false);
  const [phoneValidation, setPhoneValidation] = useState<boolean>(false);
  const [streetValidation, setStreetValidation] = useState<boolean>(true);
  const [houseNumberValidation, setHouseNumberValidation] = useState<boolean>(true);
  const [postalCodeValidation, setPostalCodeValidation] = useState<boolean>(true);
  const [cityValidation, seCityValidation] = useState<boolean>(true);
  const [countryIdValidation, setCountryIdValidation] = useState<boolean>(true);
  const [wasFormSubmited, setWasFormSubmited] = useState<boolean>(false);
  const [shouldSubmitTheForm, setShouldSubmitTheForm] = useState<boolean>(false);
  const [selectedBloodType, setSelectedBloodType] = useState<string>('');
  const [selectedRelationStatus, setSelectedRelationStatus] = useState<string>('');
  const bloodTypeOptions = getBloodTypeOptions(translations);
  const relationshipStatusOptions = getRelationshipStatusOptions(translations);
  const lang = localStorage.getItem('lang') || 'en';


  const onSaveCustomerHandler = () => {
    setWasFormSubmited(true);
    setShouldSubmitTheForm(true);
    let nameRegex = /^[\p{L}\p{M}\s\-']{2,30}$/u;
    let emailRegex = /^[_a-z0-9-]+(\.[_a-z0-9-]+)*(\+[a-z0-9-]+)?@[a-z0-9-]+(\.[a-z0-9-]+)*$/i;
    let numberRegex = /^\d+$/;
    let notEmptyRegex = /\S/;

    setPersonFirstnameValidation(!nameRegex.test(personFirstname));
    setPersonLastnameValidation(!nameRegex.test(personLastname));
    setPersonGenderIdValidation(undefined === personGenderId);
    setPersonBirthdateValidation(undefined === personBirthdate);
    if (personEmail?.trim() !== '') {
      setPersonEmailValidation(!emailRegex.test(personEmail));
    } else {
      setPersonEmailValidation(false);
    }
    setPhoneValidation(!isValid);
    if (!hasOtherInputsUpdate) {
      setStreetValidation(false);
      setHouseNumberValidation(false);
      setPostalCodeValidation(false);
      seCityValidation(false);
      setCountryIdValidation(false);
    } else {
      setStreetValidation(!notEmptyRegex.test(street));
      setHouseNumberValidation(!numberRegex.test(houseNumber));
      setPostalCodeValidation(!numberRegex.test(postalCode));
      seCityValidation(!notEmptyRegex.test(city));
      setCountryIdValidation(!notEmptyRegex.test(countryId));
    }
  };

  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchCountries());
    dispatch(fetchGenders());
  }, [dispatch]);

  useEffect(() => {
    if (customerAddingStatus === 'fulfilled') {
      dispatch(fetchCustomers());
      setPersonFirstname('');
      setPersonLastname('');
      setPersonGenderId(undefined);
      setPersonBirthdate(undefined);
      setPersonEmail('');
      setPersonMobilePhone('');
      setStreet('');
      setHouseNumber('');
      setPostalCode('');
      setCity('');
      setCountryId('');
      setPersonIdentityNumber('');
      setPersonHeight('');
      setPersonWeight('');
      setIsAddingPerson(false);
      setIsCallingAddPersonApi(false);
      toast.success(translations('placeholder.successfully_added'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      setShouldSubmitTheForm(false);
      dispatch(resetCustomerAddingStatus());
    } else if (customerAddingStatus === 'rejected') {
      setShouldSubmitTheForm(false);
      setIsCallingAddPersonApi(false);
      dispatch(resetCustomerAddingStatus());
    } else if (customerAddingStatus === 'pending') {
      setIsCallingAddPersonApi(true);
    }
  }, [customerAddingStatus, dispatch]);

  useEffect(() => {
    const valid =
      personFirstnameValidation ||
      personLastnameValidation ||
      personGenderIdValidation ||
      personBirthdateValidation ||
      personEmailValidation ||
      phoneValidation ||
      streetValidation ||
      houseNumberValidation ||
      postalCodeValidation ||
      cityValidation ||
      countryIdValidation;
    if (valid) {
      setWasFormSubmited(false);
    }
    if (valid && wasFormSubmited && shouldSubmitTheForm) {
      let validationData: IValidationData[] = [
        { name: translations('first_name'), value: personFirstnameValidation },
        { name: translations('last_name'), value: personLastnameValidation },
        { name: translations('gender'), value: personGenderIdValidation },
        { name: translations('birthdate'), value: personBirthdateValidation },
        { name: translations('email'), value: personEmailValidation },
        { name: translations('phone.number'), value: phoneValidation },
        { name: translations('street.address'), value: streetValidation },
        { name: translations('house.number'), value: houseNumberValidation },
        { name: translations('postal_code'), value: postalCodeValidation },
        { name: translations('city.address'), value: cityValidation },
        { name: translations('country'), value: countryIdValidation },
      ];
      let data = validationData.filter((x: IValidationData) => x.value === true);
      if (data.length === 1) {
        if (data[0].name === translations('phone.number') && personMobilePhone.trim().length > 0) {
          toast.error(translations('phone.error'), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        } else {
          toast.error(`${data[0]?.name} ${translations('error.this.field.is.required')}`, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
      } else {
        toast.error(translations('error.please_fill_all_required_fields'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    }

    if (!valid && wasFormSubmited && shouldSubmitTheForm) {
      type DetailsType = {
        height?: number;
        weight?: number;
        relation_status?: string;
        blood_type?: string;
      };
      let details: DetailsType = {};

      if (personHeight || personWeight || selectedRelationStatus || selectedBloodType) {
        details = {
          height: personHeight ? parseInt(personHeight) : undefined,
          weight: personWeight ? Number(personWeight) : undefined,
          relation_status: selectedRelationStatus || undefined,
          blood_type: selectedBloodType || undefined,
        };
      }

      const payload = {
        personFirstname: personFirstname.trim(),
        personLastname: personLastname.trim(),
        personGenderId: personGenderId,
        personBirthdate: new Date(personBirthdate.getTime() + 60 * 60 * 1000 * 6),
        personEmail: personEmail && personEmail?.trim() !== '' ? personEmail?.trim() : undefined,
        personMobilePhone:
          personMobilePhone && personMobilePhone.trim() !== ''
            ? personMobilePhone.trim()
            : undefined,
        street: street && street.trim() !== '' ? street.trim() : undefined,
        houseNumber: houseNumber && houseNumber.trim() !== '' ? houseNumber.trim() : undefined,
        postalCode: postalCode && postalCode !== '' ? parseInt(postalCode) : undefined,
        city: city && city.trim() !== '' ? city.trim() : undefined,
        countryId: countryId && countryId.trim() !== '' ? countryId.trim() : undefined,
        personIdentityNumber:
          personIdentityNumber && personIdentityNumber.trim() !== ''
            ? personIdentityNumber.trim()
            : undefined,

        language: language,
        ...(Object.keys(details).length > 0 ? { details } : {}),
      };

      dispatch(addCustomer(payload));
    }
  }, [
    personFirstnameValidation,
    personLastnameValidation,
    personGenderIdValidation,
    personBirthdateValidation,
    personEmailValidation,
    phoneValidation,
    streetValidation,
    houseNumberValidation,
    postalCodeValidation,
    cityValidation,
    countryIdValidation,
    wasFormSubmited,
    shouldSubmitTheForm,
    personHeight,
    personWeight,
  ]);
  const [isValid] = useState(true);

  useEffect(() => {
    const check =
      street === '' && houseNumber === '' && postalCode === '' && city === '' && countryId === '';
    setHasOtherInputsUpdate(!Boolean(check));
  }, [street, houseNumber, postalCode, city, countryId]);

  const handleAddressValidation = (inputValidation: boolean) => {
    if (!hasOtherInputsUpdate) {
      return false;
    } else {
      return inputValidation;
    }
  };

  const countryOptions = countries.map((country) => ({
    id: country.countrycode,
    label: country.country,
  }));

  return (
    <div className={classes.container}>
      <div className={classes.sectionHeader}>{translations('basics')}</div>
      <div className={classes.divider} />
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <CustomTextField
            label={translations('first_name') ?? ''}
            value={personFirstname}
            onChange={(e) => {
              const value = e?.target?.value;
              setPersonFirstname(value);
              const nameRegex = /^[\p{L}\p{M}\s\-']{2,30}$/u;
              setPersonFirstnameValidation(!nameRegex.test(value));
            }}
            errorValidation={personFirstnameValidation}
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            label={translations('last_name') ?? ''}
            value={personLastname}
            onChange={(e) => {
              const value = e?.target?.value;
              setPersonLastname(value);
              const nameRegex = /^[\p{L}\p{M}\s\-']{2,30}$/u;
              setPersonLastnameValidation(!nameRegex.test(value));
            }}
            errorValidation={personLastnameValidation}
          />
        </div>
      </div>
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <CustomDropdown
            label={translations('gender')}
            placeholder={translations('placeholder.choose_gender')}
            options={genders
              .filter(
                (g) =>
                  parseInt(g.gender_id?.toString()) === 1 || parseInt(g.gender_id?.toString()) === 2
              )
              .map((gender) => {
                const result: IIdAndLabel = {
                  id: gender.gender_id,
                  label: translations(gender.gender?.toLowerCase()),
                };
                return result;
              })}
            value={personGenderId}
            // onChange={(e) => setPersonGenderId(parseInt(e?.target?.value))}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              setPersonGenderId(value);
              setPersonGenderIdValidation(false);
            }}
            errorValidation={personGenderIdValidation}
          />
        </div>
        <div className={classes.field}>
          <ModalDatePickerNewDesign
            placeholder={translations('select').toString()}
            label={translations('date_of_birth') ?? ''}
            value={personBirthdate}
            errorValidation={personBirthdateValidation}
            // onChange={(date: Date) => setPersonBirthdate(date)}
            onChange={(date: Date) => {
              setPersonBirthdate(date);
              setPersonBirthdateValidation(false);
            }}
          />
        </div>
      </div>
      <div className={classes.sectionHeader}>
        {translations('additional.informations')}
        <span className={classes.optionalLabel}> ({translations("label.optional")})</span>
      </div>
      <div className={classes.divider} />
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <CustomTextField
            label={translations('height') ?? ''}
            value={personHeight}
            onChange={(e) => setPersonHeight(e.target.value)}
            unit="cm"
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            label={translations('weight') ?? ''}
            value={personWeight}
            onChange={(e) => setPersonWeight(e.target.value)}
            unit="kg"
          />
        </div>
      </div>
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <CustomDropdown
            label={translations('label_blood_types')}
            placeholder={translations('select')}
            options={bloodTypeOptions}
            value={selectedBloodType}
            onChange={(event) => setSelectedBloodType(event.target.value)}
          />
        </div>
        <div className={classes.field}>
          <CustomDropdown
            label={translations('label_relationship_status')}
            placeholder={translations('select')}
            options={relationshipStatusOptions}
            value={selectedRelationStatus}
            onChange={(event) => setSelectedRelationStatus(event.target.value)}
          />
        </div>
      </div>
      <div className={classes.sectionHeader}>
        {translations('others')}
        <span className={classes.optionalLabel}> ({translations("label.optional")})</span>
      </div>
      <div className={classes.divider} />
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <p className={classes.mobilePhoneLabel}>{translations('mobile_number')}</p>
          <PhoneInput
            style={{ width: 'auto', height: 46 }}
            flagComponent={(props: any) => <div>{props.countryName}</div>}
            international
            countryCallingCodeEditable={false}
            defaultCountry="DE"
            placeholder="Phone"
            value={personMobilePhone}
            // onChange={(val: E164Number | undefined) => setPersonMobilePhone(String(val))}
            onChange={(val: E164Number | undefined) => {
              const value = String(val);
              setPersonMobilePhone(value);
              setPhoneValidation(!isValidNumber(value));
            }}
            // labels={language === "de" ? de : undefined}
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            label={translations('email') ?? ''}
            value={personEmail}
            onChange={(e) => setPersonEmail(e?.target?.value)}
            errorValidation={personEmailValidation}
          />
        </div>
      </div>
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <CustomTextField
            label={translations('street') ?? ''}
            value={street}
            onChange={(e) => {
              const value = e.target.value;
              setStreet(value);
              const notEmptyRegex = /\S/;
              setStreetValidation(!notEmptyRegex.test(value));
            }}
            errorValidation={handleAddressValidation(streetValidation)}
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            label={translations('house_nr') ?? ''}
            value={houseNumber}
            onChange={(e) => {
              const value = e.target.value;
              setHouseNumber(value);
              let numberRegex = /^\d+$/;
              setHouseNumberValidation(!numberRegex.test(value));
            }}
            errorValidation={handleAddressValidation(houseNumberValidation)}
          />
        </div>
      </div>
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <CustomTextField
            label={translations('postal_code') ?? ''}
            value={postalCode}
            onChange={(e) => {
              const value = e.target.value;
              setPostalCode(value);
              let numberRegex = /^\d+$/;
              setPostalCodeValidation(!numberRegex.test(value));
            }}
            errorValidation={handleAddressValidation(postalCodeValidation)}
            maxWidth={'100%'}
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            label={translations('city') ?? ''}
            value={city}
            // onChange={(e) => setCity(e.target.value)}
            onChange={(e) => {
              const value = e.target.value;
              setCity(value);
              const notEmptyRegex = /\S/;
              seCityValidation(!notEmptyRegex.test(value));
            }}
            errorValidation={handleAddressValidation(cityValidation)}
          />
        </div>
      </div>
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <CustomDropdown
            label={translations('country')}
            options={[{ id: '', label: 'Select' }, ...countryOptions]}
            value={countryId}
            maxWidth={'324px'}
            onChange={(e) => {
              const value = e?.target?.value;
              setCountryId(value);
              const notEmptyRegex = /\S/;
              setCountryIdValidation(!notEmptyRegex.test(value));
            }}
            errorValidation={handleAddressValidation(countryIdValidation)}
          />
        </div>
        <div className={classes.field}>
          {/* <CustomDropdown
            label={translations('nationality')}
            placeholder="Choose Nationality"
            options={nationalities.map((nationality) => {
              const result: IIdAndLabel = {
                id: nationality.nationality_id,
                label: nationality.nationality_name,
              };
              return result;
            })}
            value={personNationalityId}
            onChange={(e) => setPersonNationalityId(parseInt(e?.target?.value))}
            errorValidation={nationalityIdValidation}
            maxWidth="100%"
          /> */}
        </div>
      </div>
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <CustomTextField
            label={translations('passport_number') ?? ''}
            value={personIdentityNumber}
            onChange={(e) => setPersonIdentityNumber(e.target.value)}
          />
        </div>
        <div className={classes.field}>
          <CustomDropdown
            label={translations('language') ?? ''}
            value={language ?? ''}
            onChange={(e) => setLanguage(e.target.value)}
            options={['de', 'en', 'fr', 'es', 'nl'].map((lang) => {
              const result: IIdAndLabel = {
                id: lang,
                label: translations(lang),
              };
              return result;
            })}
            maxWidth={'453px'}
            placeholder=""
          />
        </div>
      </div>
      <div className={classes.divider} />
      <div className={classes.buttonsContainer}>
        {isCallingAddPersonApi ? (
          <Spinner />
        ) : (
          <PrimaryButtonV2
            text={translations('save')}
            onClick={onSaveCustomerHandler}
            containerStyle={{
              marginRight: 20,
            }}
            buttonStyle={{
              minWidth: 270,
              height: 60,
              borderWidth: 1,
            }}
          />
        )}
        <PrimaryButtonV2
          invertedColors
          text={translations('button.cancel')}
          onClick={() => setIsAddingPerson(false)}
          containerStyle={{
            marginRight: 10,
          }}
          buttonStyle={{
            minWidth: 270,
            height: 60,
            borderWidth: 1,
          }}
        />
      </div>
    </div>
  );
};

export { AddCustomer };
